import { useState, useRef, useCallback, useEffect } from 'react';
import {
  getMfaOptions, chooseEmailMfa, chooseSmsMfa, enrollTotp, verifyTotpEnrollment, getProfile,
} from '@/services/rbac-auth.service';
import { useAuthStore } from '@/stores/auth.store';
import type { MfaEnrollTotpResponse } from '@/types/rbac-auth.types';

export type MfaSetupStep = 'choose' | 'show-totp';

export function useMfaSetup() {
  const [step, setStep] = useState<MfaSetupStep>('choose');
  const [showEmail, setShowEmail] = useState(true);
  const [showTotp, setShowTotp] = useState(true);
  const [showSms, setShowSms] = useState(false);
  const [totp, setTotp] = useState<MfaEnrollTotpResponse | null>(null);
  const [savedCodes, setSavedCodes] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const enrollSent = useRef(false);

  useEffect(() => {
    let active = true;
    getMfaOptions()
      .then((o) => {
        if (!active) return;
        const allowed = o.allowedMethods ?? [];
        if (allowed.length > 0) {
          setShowEmail(allowed.includes('EMAIL_OTP'));
          setShowTotp(allowed.includes('TOTP'));
          setShowSms(allowed.includes('SMS_OTP'));
        }
      })
      .catch(() => { /* fail-open: defaults email+totp */ });
    return () => { active = false; };
  }, []);

  const refreshProfile = useCallback(async () => {
    const p = await getProfile();
    useAuthStore.getState().setFromProfile(p);
  }, []);

  const chooseEmail = useCallback(async () => {
    setError(null); setLoading(true);
    try { await chooseEmailMfa(); await refreshProfile(); }
    catch { setError('No se pudo activar el correo. Reintentá.'); }
    finally { setLoading(false); }
  }, [refreshProfile]);

  const chooseSms = useCallback(async () => {
    setError(null); setLoading(true);
    try { await chooseSmsMfa(); await refreshProfile(); }
    catch { setError('No se pudo activar el SMS. Reintentá.'); }
    finally { setLoading(false); }
  }, [refreshProfile]);

  const startTotp = useCallback(async () => {
    if (enrollSent.current) return;
    enrollSent.current = true;
    setError(null); setLoading(true);
    try { const r = await enrollTotp(); setTotp(r); setStep('show-totp'); }
    catch { setError('No se pudo iniciar el enrolamiento. Reintentá.'); enrollSent.current = false; }
    finally { setLoading(false); }
  }, []);

  const verifyTotp = useCallback(async () => {
    if (!totp) return;
    setError(null); setLoading(true);
    try { await verifyTotpEnrollment(code, totp.enrollmentToken); await refreshProfile(); }
    catch { setError('Código inválido. Reintentá.'); setCode(''); }
    finally { setLoading(false); }
  }, [totp, code, refreshProfile]);

  const back = useCallback(() => {
    setStep('choose'); setTotp(null); setSavedCodes(false); setCode(''); setError(null);
    enrollSent.current = false;
  }, []);

  return {
    step, showEmail, showTotp, showSms, totp, savedCodes, setSavedCodes,
    code, setCode, error, loading, chooseEmail, chooseSms, startTotp, verifyTotp, back,
  };
}
